import React, { useEffect, useState } from 'react';

import LoadingIndicator from '@magoosh/layouts/lib/loading_indicator';
import { DangerBadge, DefaultBadge, SuccessBadge, WarningBadge } from '@magoosh/lib/badge';
import { pluralize } from 'utilities/pluralize';

import * as api from '@admin/app/api';

interface Props {
  period: {
    from: number;
    to: number;
  };
  rateable: {
    id: number;
    type: string;
  };
}

const Ratings: React.FC<Props> = (props) => {
  const [page, setPage] = useState(null);
  const [ratingsPages, setRatingsPages] = useState([]);
  const [ratingsSummary, setRatingsSummary] = useState(null);

  const perPage = 25;

  useEffect(() => {
    if (!page) return;
    if (ratingsPages[page]) return;

    fetchPage().then((ratings) => {
      const updatedRatingsPages = ratingsPages.map((ratingsPage) => ratingsPage ? [...ratingsPage] : null);
      updatedRatingsPages[page - 1] = ratings;
      setRatingsPages(updatedRatingsPages);
    });
  }, [page]);

  useEffect(() => {
    if (ratingsSummary) return;

    fetchSummary().then((summary) => {
      setRatingsSummary(summary);
      const pageCount = Math.ceil(summary.count / perPage);
      setRatingsPages(Array(pageCount).fill(null));
      setPage(1);
    });
  }, []);

  const fetchPage = () => {
    return api.fetchRatings(
      props.rateable.id,
      props.rateable.type,
      {
        page,
        per_page: perPage,
        period_end: props.period.to,
        period_start: props.period.from,
        with_feedback: 'true'
      }
    );
  };

  const fetchSummary = () => {
    return api.fetchRatingsSummary(
      props.rateable.id,
      props.rateable.type,
      {
        period_end: props.period.to,
        period_start: props.period.from
      }
    );
  };

  if (ratingsSummary) {
    return (
      <div className="d-flex flex-column gap-s">
        <div className="d-flex gap-xs">
          This version has received a like rate of
          {(() => {
            const percentage = Math.round(ratingsSummary.rating * 100);

            if (ratingsSummary.count === 0 || ratingsSummary.rating === 1.0) {
              return <DefaultBadge>{percentage}%</DefaultBadge>
            } else if (ratingsSummary.upperConfidenceBound80 <= 0.8) {
              return <DangerBadge>{percentage}%</DangerBadge>
            } else if (ratingsSummary.upperConfidenceBound80 <= 0.9) {
              return <WarningBadge>{percentage}%</WarningBadge>
            } else {
              return <SuccessBadge>{percentage}%</SuccessBadge>
            }
          })()}
          from {pluralize(ratingsSummary.count, 'rating')}.
        </div>

        TODO - paginated ratings with feedback
      </div>
    );
  } else {
    return (
      <div className="d-flex justify-content-center u-margin-T-l">
        <LoadingIndicator />
      </div>
    );
  }
};

export default Ratings;
